
import { useEffect, useState } from 'react'
import { isSupabaseConfigured, supabase } from '@/lib/supabase'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AlertCircle, Info } from 'lucide-react'

export const SupabaseStatus = () => {
  const [showWarning, setShowWarning] = useState(false)
  const [errorMessage, setErrorMessage] = useState('Unable to connect to database. Please try again later.')
  const [isConfigError, setIsConfigError] = useState(false)
  const [connectionAttempted, setConnectionAttempted] = useState(false)
  const [debugInfo, setDebugInfo] = useState<any>(null)
  
  useEffect(() => {
    // Test the connection by making a simple query
    const testConnection = async () => {
      try {
        // Collect environment info for debugging
        const envInfo = {
          hasUrlInMeta: Boolean(import.meta.env.VITE_SUPABASE_URL),
          hasKeyInMeta: Boolean(import.meta.env.VITE_SUPABASE_ANON_KEY),
          urlType: typeof import.meta.env.VITE_SUPABASE_URL,
          keyType: typeof import.meta.env.VITE_SUPABASE_ANON_KEY,
          hasLocalUrl: Boolean(localStorage.getItem('supabase_url')),
          hasLocalKey: Boolean(localStorage.getItem('supabase_key')),
        };
        
        setDebugInfo(envInfo);
        
        // First check if the config exists
        if (!isSupabaseConfigured()) {
          console.error('Supabase environment variables are missing');
          setErrorMessage('Database configuration is missing. Please try using the Direct Connection option in Settings.');
          setIsConfigError(true);
          setShowWarning(true);
          setConnectionAttempted(true);
          return;
        }
        
        // Try a simple query to check if connection works
        const { error } = await supabase.from('subscribers').select('count').limit(0);
        
        if (error) {
          console.error('Supabase query error:', error);
          setErrorMessage('Database connection error: ' + error.message);
          setIsConfigError(error.message?.includes('JWT') || error.message?.includes('key'));
          setShowWarning(true);
          setConnectionAttempted(true);
          return;
        }
        
        setShowWarning(false);
        setConnectionAttempted(true);
        console.log('Supabase connection successful');
      } catch (error) {
        console.error('Supabase connection error:', error);
        setErrorMessage('Unable to connect to database. Please check network connection and try using the Direct Connection option in Settings.');
        setShowWarning(true);
        setConnectionAttempted(true);
      }
    };
    
    testConnection();
  }, [])
  
  // Only show when there's an issue and we've attempted connection
  if (!showWarning || !connectionAttempted) return null
  
  return (
    <Alert 
      variant="destructive" 
      className="fixed bottom-4 right-4 max-w-md z-50"
    >
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>Database Connection Error</AlertTitle>
      <AlertDescription>
        <p>{errorMessage}</p>
        {isConfigError && (
          <p className="mt-1 text-xs font-semibold">
            Try using the Direct Connection option in Settings to manually set your Supabase credentials.
          </p>
        )}
        {debugInfo && (
          <details className="mt-2 text-xs">
            <summary className="cursor-pointer font-semibold">Debug Information</summary>
            <pre className="mt-1 p-2 bg-slate-800 text-white rounded overflow-x-auto">
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          </details>
        )}
      </AlertDescription>
    </Alert>
  )
}
